import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import genres from '../../shared/constants/genres';
import supportedLanguages from '../../config/languages';
import {getFreshIdToken} from '../../utils/authUtil';
import uxAnalyticsUtil from '../../utils/uxAnalyticsUtil';
import DiscoveryCard from './DiscoveryCard';
import {discoveryPath, parseDiscoveryState, DISCOVERY_AI_FILTERS} from './discoveryState';
import {useDiscoveryExperimentTracking} from './experimentClient';

const Page = styled.main`
  width: 100%; max-width: 1180px; margin: 0 auto; padding: 28px 22px 100px;
  color: ${({theme}) => theme.navigation.heading};
  @media (max-width: 767px) { padding: 20px 4px 110px; }
`;
const Hero = styled.header`
  padding: 26px 30px; border: 1px solid ${({theme}) => theme.navigation.border};
  border-radius: 18px; background: ${({theme}) => theme.navigation.surface};
  box-shadow: 0 8px 30px ${({theme}) => theme.navigation.shadow};
  h1 { font-size: clamp(1.65rem, 3vw, 2.5rem); line-height: 1.12; margin: 0 0 7px; }
  p { margin: 0; color: ${({theme}) => theme.navigation.text}; }
  @media (max-width: 767px) { padding: 20px 16px; }
`;
const Search = styled.form`
  display: flex; gap: 8px; margin-top: 20px;
  input { flex: 1; min-width: 0; padding: 12px 14px; border: 1px solid ${({theme}) => theme.navigation.border}; border-radius: 9px; background: ${({theme}) => theme.navigation.surface} !important; color: ${({theme}) => theme.navigation.heading} !important; font: inherit; }
  input::placeholder { color: ${({theme}) => theme.navigation.mutedText}; opacity: 1; }
  @media (max-width: 767px) { margin-top: 16px; }
`;
const Button = styled.button`
  padding: 10px 15px; border: 1px solid ${({theme}) => theme.navigation.accent};
  border-radius: 9px; color: ${({theme}) => theme.navigation.tabActiveText};
  background: ${({theme}) => theme.navigation.accent}; font: inherit; font-weight: 700;
  cursor: pointer; &:focus-visible { outline: 3px solid ${({theme}) => theme.navigation.focus}; outline-offset: 2px; }
`;
const GhostButton = styled(Button)`
  color: ${({theme}) => theme.navigation.accent}; background: transparent;
`;
const ScopeNav = styled.nav`
  display: flex; gap: 8px; flex-wrap: wrap; margin: 18px 0;
  a { padding: 9px 14px; border-radius: 24px; border: 1px solid ${({theme}) => theme.navigation.border}; color: ${({theme}) => theme.navigation.heading}; background: ${({theme}) => theme.navigation.surface}; font-weight: 700; }
  a[aria-current='page'] { background: ${({theme}) => theme.navigation.activeBackground}; color: ${({theme}) => theme.navigation.activeText}; border-color: ${({theme}) => theme.navigation.accent}; }
`;
const Panel = styled.section`
  padding: 22px; border: 1px solid ${({theme}) => theme.navigation.border};
  border-radius: 14px; background: ${({theme}) => theme.navigation.surface};
  h2 { font-size: 1.3rem; margin: 0 0 4px; }
  p { color: ${({theme}) => theme.navigation.text}; }
  @media (max-width: 767px) { padding: 15px; }
`;
const Section = styled.section` margin-top: 25px; `;
const SectionHead = styled.div`
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin: 0 0 12px;
  h2 { margin: 0; font-size: 1.35rem; }
  p { margin: 2px 0 0; color: ${({theme}) => theme.navigation.text}; font-size: 0.82rem; }
  a { color: ${({theme}) => theme.navigation.accent}; font-weight: 700; }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px;
  @media (max-width: 920px) { grid-template-columns: 1fr; }
`;
const GenreGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px;
  button { background: ${({theme}) => theme.navigation.raisedSurface}; color: ${({theme}) => theme.navigation.heading}; border: 1px solid ${({theme}) => theme.navigation.border}; border-radius: 8px; padding: 10px 13px; cursor: pointer; font: inherit; }
`;
const ResultsLayout = styled.div`
  display: grid; grid-template-columns: 260px minmax(0, 1fr); gap: 20px;
  @media (max-width: 767px) { grid-template-columns: 1fr; }
`;
const FilterPanel = styled(Panel)`
  align-self: start; h2 { font-size: 1rem; }
  @media (max-width: 767px) { display: none; }
`;
const FilterFields = styled.div`
  display: flex; flex-direction: column; gap: 15px;
  label { display: block; font-weight: 700; font-size: 0.85rem; margin-bottom: 5px; }
  select, input[type='search'] { width: 100%; padding: 9px; border: 1px solid ${({theme}) => theme.navigation.border}; border-radius: 8px; background: ${({theme}) => theme.navigation.surface} !important; color: ${({theme}) => theme.navigation.heading} !important; font: inherit; }
  input::placeholder { color: ${({theme}) => theme.navigation.mutedText}; opacity: 1; }
  .choices { max-height: 200px; overflow-y: auto; padding: 3px; }
  .choice { display: flex; gap: 8px; align-items: center; padding: 4px 0; font-weight: 400; }
  .choice input { accent-color: ${({theme}) => theme.navigation.accent}; }
  small { color: ${({theme}) => theme.navigation.text}; }
`;
const Toolbar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px;
  h2 { font-size: 1.25rem; margin: 0; }
  p { margin: 2px 0 0; color: ${({theme}) => theme.navigation.text}; }
  select { padding: 8px; border: 1px solid ${({theme}) => theme.navigation.border}; border-radius: 8px; background: ${({theme}) => theme.navigation.surface}; color: ${({theme}) => theme.navigation.heading}; }
  .visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
  @media (max-width: 767px) {
    display: block;
    > div:last-child { display: flex; align-items: center; gap: 8px; margin-top: 10px; }
  }
`;
const MobileFilterButton = styled(GhostButton)`
  display: none; @media (max-width: 767px) { display: inline-block; }
`;
const Scrim = styled.div`
  position: fixed; inset: 0; z-index: 1000; background: ${({theme}) => theme.navigation.scrim};
`;
const Sheet = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; max-height: 90vh; overflow-y: auto;
  padding: 20px 18px max(20px, env(safe-area-inset-bottom)); border-radius: 18px 18px 0 0;
  background: ${({theme}) => theme.navigation.surface}; color: ${({theme}) => theme.navigation.heading};
  h2 { margin-top: 0; } footer { position: sticky; bottom: 0; display: flex; gap: 8px; padding-top: 14px; background: ${({theme}) => theme.navigation.surface}; }
`;
const Chips = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px;
  button { border: 1px solid ${({theme}) => theme.navigation.border}; border-radius: 20px; padding: 6px 10px; color: ${({theme}) => theme.navigation.activeText}; background: ${({theme}) => theme.navigation.activeBackground}; cursor: pointer; }
`;
const Notice = styled(Panel)` margin-top: 12px; `;

const AI_LABELS = {
  all: 'DiscoveryAiAll', human: 'DiscoveryAiHuman', assisted: 'DiscoveryAiAssisted',
  generated: 'DiscoveryAiGenerated', no_generated: 'DiscoveryAiNoGenerated', undeclared: 'DiscoveryAiUndeclared'
};
const optionsFrom = (state, cursor, limit = 20) => {
  const params = new URLSearchParams({scope: state.scope, sort: state.sort, ai: state.ai, limit: String(limit)});
  if (state.q) params.set('q', state.q);
  state.genres.forEach(genre => params.append('genre', genre));
  state.languages.forEach(lang => params.append('lang', lang));
  if (cursor) params.set('cursor', cursor);
  return params;
};
const requestBrowse = async (state, cursor, signal, limit) => {
  const token = await getFreshIdToken();
  const url = `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/ereader/discover/browse?${optionsFrom(state, cursor, limit)}`;
  const response = await fetch(url, {headers: {'br-token': token}, signal});
  if (!response.ok) throw new Error('Discovery request failed');
  return response.json();
};

const FilterControls = ({state, update, hideGenerated, t, language, idPrefix}) => {
  const [genreSearch, setGenreSearch] = useState('');
  const [languageSearch, setLanguageSearch] = useState('');
  const visibleGenres = genres.filter(genre => genre.toLowerCase().includes(genreSearch.toLowerCase()));
  const languageNames = new Intl.DisplayNames([language], {type: 'language'});
  const visibleLanguages = supportedLanguages
    .map(item => item.alpha2).filter(code => code && languageNames.of(code).toLowerCase().includes(languageSearch.toLowerCase()));
  const toggle = (key, value) => update({...state, [key]: state[key].includes(value)
    ? state[key].filter(item => item !== value) : [...state[key], value]});
  return <FilterFields>
    <div><label htmlFor={`${idPrefix}-genre-search`}>{t('DiscoveryGenres')}</label>
      <input id={`${idPrefix}-genre-search`} type="search" value={genreSearch} onChange={event => setGenreSearch(event.target.value)} placeholder={t('DiscoveryFindGenre')} />
      <div className="choices" role="group" aria-label={t('DiscoveryGenres')}>{visibleGenres.map(genre => <label className="choice" key={genre}>
        <input type="checkbox" checked={state.genres.includes(genre)} onChange={() => toggle('genres', genre)} />{genre}
      </label>)}</div></div>
    <div><label htmlFor={`${idPrefix}-languages-search`}>{t('Languages')}</label>
      <input id={`${idPrefix}-languages-search`} type="search" value={languageSearch} onChange={event => setLanguageSearch(event.target.value)} placeholder={t('DiscoveryFindLanguage')} />
      <div id={`${idPrefix}-languages`} className="choices" role="group" aria-label={t('Languages')}>{visibleLanguages.map(code => <label className="choice" key={code}>
        <input type="checkbox" checked={state.languages.includes(code)} onChange={() => toggle('languages', code)} />{languageNames.of(code)}
      </label>)}</div></div>
    <div><label htmlFor={`${idPrefix}-ai`}>{t('DiscoveryAiUse')}</label>
      <select id={`${idPrefix}-ai`} value={state.ai} onChange={event => update({...state, ai: event.target.value})}>
        {DISCOVERY_AI_FILTERS.map(value => <option key={value} value={value} disabled={hideGenerated && value === 'generated'}>{t(AI_LABELS[value])}</option>)}
      </select>{hideGenerated && <small>{t('AiManuscriptPreferenceHidden')}</small>}
      <small><Link to="/account/profile">{t('AiManuscriptPreferenceSettings')}</Link></small></div>
  </FilterFields>;
};

FilterControls.propTypes = {
  state: PropTypes.shape({
    genres: PropTypes.arrayOf(PropTypes.string).isRequired,
    languages: PropTypes.arrayOf(PropTypes.string).isRequired,
    ai: PropTypes.string.isRequired
  }).isRequired,
  update: PropTypes.func.isRequired,
  hideGenerated: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  idPrefix: PropTypes.string.isRequired
};

const DiscoveryExperience = () => {
  const trackExperiment = useDiscoveryExperimentTracking();
  const {t, i18n} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const userProfile = useSelector(store => store.user.userProfile);
  const state = useMemo(() => parseDiscoveryState(location.search), [location.search]);
  const [query, setQuery] = useState(state.q);
  const [result, setResult] = useState({items: [], total: 0, nextCursor: null});
  const [home, setHome] = useState({recent: [], paid: [], forYou: []});
  const [homeErrors, setHomeErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [draft, setDraft] = useState(state);
  const [retry, setRetry] = useState(0);
  const [showAllGenres, setShowAllGenres] = useState(false);
  const filterButton = useRef(null);
  const filterSheet = useRef(null);
  const hideGenerated = userProfile?.showAiGeneratedManuscripts === false;
  const navigate = useCallback(next => {
    uxAnalyticsUtil.trackEvent({category: 'Discover', action: 'updated-results', label: next.scope});
    history.push(discoveryPath({...next, page: 1, cursor: '', trail: []}));
  }, [history]);
  useEffect(() => setQuery(state.q), [state.q]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(false);
    if (state.view === 'home') {
      const base = {...state, view: 'results'};
      Promise.allSettled([
        requestBrowse({...base, scope: 'all'}, null, controller.signal, 8),
        requestBrowse({...base, scope: 'paid'}, null, controller.signal, 8),
        userProfile?.preferredGenres?.length ? requestBrowse({...base, scope: 'for-you'}, null, controller.signal, 8) : Promise.resolve({items: []})
      ]).then(([recent, paid, forYou]) => {
        if (controller.signal.aborted) return;
        const seen = new Set();
        const pick = (books, keepIfAllSeen = false) => {
          const unique = (books || []).filter(book => !seen.has(book._id)).slice(0, 2);
          const chosen = unique.length || !keepIfAllSeen ? unique : (books || []).slice(0, 2);
          chosen.forEach(book => seen.add(book._id));
          return chosen;
        };
        const chosenForYou = pick(forYou.value?.items);
        const chosenRecent = pick(recent.value?.items, true);
        const chosenPaid = pick(paid.value?.items, true);
        setHome({recent: chosenRecent, paid: chosenPaid, forYou: chosenForYou});
        setHomeErrors({recent: recent.status === 'rejected', paid: paid.status === 'rejected', forYou: forYou.status === 'rejected'});
        setLoading(false);
      });
    } else if (state.page === 1 || state.cursor) {
      requestBrowse(state, state.cursor, controller.signal).then(data => {
        setResult(data); setLoading(false);
        if (data.total === 0) {
          uxAnalyticsUtil.trackEvent({category: 'Discover', action: 'zero-results', label: state.scope});
          trackExperiment('zero_results');
        }
      }).catch(() => {if (!controller.signal.aborted) {setError(true); setLoading(false);}});
    } else {
      history.replace(discoveryPath({...state, page: 1}));
    }
    return () => controller.abort();
  }, [state.view, state.scope, state.q, state.genres.join('|'), state.languages.join('|'), state.ai, state.sort, state.page, state.cursor, userProfile?._id, userProfile?.showAiGeneratedManuscripts, userProfile?.preferredGenres?.join('|'), retry]);
  const openFilters = () => {setDraft(state); setMobileFilters(true);};
  const genreLink = genre => {
    uxAnalyticsUtil.trackEvent({category: 'Discover', action: 'opened-genre', label: genre});
    navigate({...state, view: 'results', scope: 'all', genres: [genre]});
  };
  useEffect(() => {
    if (!mobileFilters) return undefined;
    const sheet = filterSheet.current;
    sheet?.querySelector('input, select, button')?.focus();
    const handleKeyDown = event => {
      if (event.key === 'Escape') {setMobileFilters(false); return;}
      if (event.key !== 'Tab' || !sheet) return;
      const focusables = [...sheet.querySelectorAll('input, select, button, a[href]')].filter(node => !node.disabled);
      if (!focusables.length) return;
      const first = focusables[0]; const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last.focus();}
      if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first.focus();}
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {document.removeEventListener('keydown', handleKeyDown); filterButton.current?.focus();};
  }, [mobileFilters]);
  const sections = [
    {key: 'forYou', title: t('titleDiscoveryRecommended'), subtitle: t('subheaderDiscoveryRecommended'), scope: 'for-you', visible: Boolean(userProfile?.preferredGenres?.length) && (loading || homeErrors.forYou || home.forYou.length > 0)},
    {key: 'recent', title: t('titleDiscoveryRecentlyAdded'), scope: 'all', visible: true},
    {key: 'paid', title: t('DiscoveryPaidAuthors'), scope: 'paid', visible: true}
  ];
  const scopeItems = [
    ['all', t('DiscoveryAll')], ['for-you', t('titleDiscoveryRecommended')], ['paid', t('DiscoveryPaidAuthors')]
  ];
  const chips = [
    ...state.genres.map(value => ({label: value, remove: () => navigate({...state, genres: state.genres.filter(item => item !== value)})})),
    ...state.languages.map(value => ({label: value, remove: () => navigate({...state, languages: state.languages.filter(item => item !== value)})})),
    ...(state.ai !== 'all' ? [{label: t(AI_LABELS[state.ai]), remove: () => navigate({...state, ai: 'all'})}] : [])
  ];
  const count = new Intl.NumberFormat(i18n.language).format(result.total);
  return <Page>
    <Hero><h1>{t('DiscoveryTitle')}</h1><p>{t('DiscoveryIntro')}</p>
      <Search onSubmit={event => {event.preventDefault(); if (query.trim()) trackExperiment('search'); navigate({...state, view: 'results', q: query.trim()});}} role="search">
        <input aria-label={t('DiscoverSearch')} placeholder={t('DiscoverSearch')} value={query} onChange={event => setQuery(event.target.value)} maxLength={100} />
        <Button type="submit">{t('DiscoverySearchAction')}</Button>
      </Search>
    </Hero>
    <ScopeNav aria-label={t('DiscoveryLists')}>
      <Link to="/discover" aria-current={state.view === 'home' ? 'page' : undefined}>{t('DiscoveryHome')}</Link>
      {scopeItems.map(([scope, label]) => <Link key={scope} to={discoveryPath({...state, view: 'results', scope, page: 1})} aria-current={state.view === 'results' && state.scope === scope ? 'page' : undefined}>{label}</Link>)}
    </ScopeNav>
    {error && <Notice role="alert">{t('DiscoveryLoadError')} <GhostButton onClick={() => setRetry(value => value + 1)}>{t('DiscoveryRetry')}</GhostButton></Notice>}
    {state.view === 'home' ? <>
      {sections.filter(section => section.visible).map(section => <Section key={section.key}>
        <SectionHead><div><h2>{section.title}</h2>{section.subtitle && <p>{section.subtitle}</p>}</div><Link onClick={() => uxAnalyticsUtil.trackEvent({category: 'Discover', action: 'opened-list', label: section.key})} to={discoveryPath({...state, view: 'results', scope: section.scope, page: 1, cursor: ''})}>{t('DiscoveryViewAll')}</Link></SectionHead>
        {loading ? <Panel role="status">{t('Loading')}…</Panel> : homeErrors[section.key] ? <Panel role="alert">{t('DiscoveryLoadError')} <GhostButton onClick={() => setRetry(value => value + 1)}>{t('DiscoveryRetry')}</GhostButton></Panel> : home[section.key].length ? <Grid>{home[section.key].map(book => <DiscoveryCard key={book._id} book={book} compact source={section.key} />)}</Grid> : <Panel>{t('DiscoveryEmptyList')}</Panel>}
      </Section>)}
      <Section><SectionHead><h2>{t('DiscoveryExploreGenres')}</h2></SectionHead><GenreGrid>{genres.slice(0, showAllGenres ? undefined : 16).map(genre => <button key={genre} onClick={() => genreLink(genre)}>{genre}</button>)}</GenreGrid>{!showAllGenres && <GhostButton style={{marginTop: 12}} onClick={() => setShowAllGenres(true)}>{t('DiscoveryShowAllGenres')}</GhostButton>}</Section>
    </> : <>
      <ResultsLayout>
        <FilterPanel><h2>{t('Filters')}</h2><FilterControls state={state} update={next => {trackExperiment('filter'); navigate({...next, view: 'results'});}} hideGenerated={hideGenerated} t={t} language={i18n.language} idPrefix="desktop-discovery" /></FilterPanel>
        <div>
          <Toolbar><div><h2>{state.scope === 'paid' ? t('DiscoveryPaidAuthors') : state.scope === 'for-you' ? t('titleDiscoveryRecommended') : t('DiscoveryAll')}</h2><p aria-live="polite">{loading ? t('Loading') : t('DiscoveryResultCount', {count: result.total, formattedCount: count})}</p></div>
            <div><MobileFilterButton ref={filterButton} type="button" onClick={openFilters}>{t('Filters')}{chips.length ? ` (${chips.length})` : ''}</MobileFilterButton>
              <label htmlFor="discovery-sort" className="visually-hidden">{t('DiscoverySort')}</label>
              <select id="discovery-sort" value={state.sort} onChange={event => navigate({...state, sort: event.target.value})}><option value="newest">{t('DiscoveryNewest')}</option><option value="shortest">{t('DiscoveryShortest')}</option></select></div>
          </Toolbar>
          {chips.length > 0 && <Chips aria-label={t('DiscoveryActiveFilters')}>{chips.map(chip => <button key={chip.label} onClick={chip.remove} aria-label={`${t('RemoveFilter')}: ${chip.label}`}>{chip.label} ×</button>)}<button onClick={() => navigate({...state, genres: [], languages: [], ai: 'all'})}>{t('RemoveFilters')}</button></Chips>}
          {loading ? <Notice role="status">{t('Loading')}…</Notice> : error ? null : result.items.length === 0 ? <Notice><h3>{t('DiscoveryNoResults')}</h3><p>{state.scope === 'for-you' && !userProfile?.preferredGenres?.length ? t('DiscoverySetPreferences') : t('DiscoveryTryBroader')}</p><GhostButton onClick={() => navigate({...state, scope: 'all', q: '', genres: [], languages: [], ai: 'all'})}>{t('RemoveFilters')}</GhostButton></Notice> : <Grid>{result.items.map(book => <DiscoveryCard key={book._id} book={book} onGenre={genreLink} source={state.scope} />)}</Grid>}
          {!loading && !error && <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 16}}>{state.page > 1 && <GhostButton onClick={() => history.push(discoveryPath({...state, page: state.page - 1, cursor: state.trail[state.trail.length - 1] || '', trail: state.trail.slice(0, -1)}))}>{t('Back')}</GhostButton>}{result.nextCursor && <GhostButton onClick={() => history.push(discoveryPath({...state, page: state.page + 1, cursor: result.nextCursor, trail: [...state.trail, state.cursor]}))}>{t('DiscoveryNextPage')}</GhostButton>}</div>}
        </div>
      </ResultsLayout>
    </>}
    {mobileFilters && <Scrim onMouseDown={event => {if (event.target === event.currentTarget) setMobileFilters(false);}}><Sheet ref={filterSheet} role="dialog" aria-modal="true" aria-label={t('Filters')}>
      <h2>{t('Filters')}</h2><FilterControls state={draft} update={setDraft} hideGenerated={hideGenerated} t={t} language={i18n.language} idPrefix="mobile-discovery" />
      <footer><GhostButton onClick={() => setMobileFilters(false)}>{t('DiscoveryCancel')}</GhostButton><Button onClick={() => {trackExperiment('filter'); navigate({...draft, view: 'results'}); setMobileFilters(false);}}>{t('DiscoveryApplyFilters')}</Button></footer>
    </Sheet></Scrim>}
  </Page>;
};

export default DiscoveryExperience;
